import { Component, Input, Output, EventEmitter, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { UserFilesService } from '../../services/user-files.service';
import { finalize } from 'rxjs/operators';
import { FileUploadStatus } from '../../models/file-upload.model';

@Component({
  selector: 'lib-file-upload',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './file-upload.component.html',
  styleUrl: './file-upload.component.css'
})
export class FileUploadComponent implements OnInit {
  @Input() userId!: string;
  @Input() orgId!: string;
  @Output() uploadDone = new EventEmitter<FileUploadStatus[]>();
  
  files: File[] = [];
  isDragging = false;
  isUploading = false;
  fileStatuses: FileUploadStatus[] = [];
  userFilesService = inject(UserFilesService)
  
  // Allowed file types
  allowedTypes = [
    'application/pdf', 
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
    'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
    'text/plain', // .txt
    'text/markdown' // .md
  ];
  
  constructor() {}
  
  ngOnInit(): void {
    if (!this.userId || !this.orgId) {
      console.error('FileUploadComponent: userId and orgId are required inputs');
    }
  }
  
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }
  
  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }
  
  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
    
    const files = event.dataTransfer?.files;
    if (files) {
      this.handleFiles(files);
    }
  }
  
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      this.handleFiles(input.files);
    }
  }
  
  handleFiles(fileList: FileList): void {
    Array.from(fileList).forEach(file => {
      if (this.isValidFile(file)) {
        this.files.push(file);
      }
    });
  }
  
  isValidFile(file: File): boolean {
    if (!this.allowedTypes.includes(file.type)) {
      alert(`File type ${file.type} is not supported.`);
      return false;
    }
    
    // 50MB max file size for optimization
    if (file.size > 50 * 1024 * 1024) {
      alert('File size exceeds 50MB limit');
      return false;
    }
    
    return true;
  }
  
  removeFile(index: number): void {
    this.files.splice(index, 1);
  }
  
  uploadFiles(): void {
    if (this.files.length === 0) return;
    
    this.isUploading = true;
    this.fileStatuses = [];
    
    let completedCount = 0;
    
    this.files.forEach((file, index) => {
      const fileStatus: FileUploadStatus = {
        fileName: file.name,
        fileType: file.type,
        url: '',
        progress: 0,
        error: null,
        createdAt: new Date(),
        userId: this.userId,
        orgCode: this.orgId
      };
      
      this.fileStatuses.push(fileStatus);
      
      this.userFilesService.uploadFile(file, this.userId, this.orgId)
        .pipe(
          finalize(() => {
            completedCount++;
            if (completedCount === this.files.length) {
              this.isUploading = false;
              this.uploadDone.emit(this.fileStatuses.filter(s => !s.error));
              this.files = [];
            }
          })
        )
        .subscribe({
          next: (progress: any) => {
            if (typeof progress === 'number') {
              fileStatus.progress = progress;
            } else {
              fileStatus.url = progress.url;
              fileStatus.progress = 100;
            }
          },
          error: (error:any) => {
            fileStatus.error = error.message;
            console.error(`Error uploading ${file.name}:`, error);
          }
        });
    });
  }
}
